import { slicedToArray as _slicedToArray, typeof as _typeof } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React__default, { useState, useEffect } from 'react';
import { useSearchContext } from '../../searchContext/SearchContextProvider.js';
import SectionAnalyticsWrapper from './SearchResultSectionAnalyticsWrapper.js';
import sortResultsByType from '../../util/sortResults.js';
import { CustomerWorkResultTaxonomy } from '../taxonomy/Taxonomies.js';
import { ListItem } from '@vp/swan';

var TAXONOMY_RESULT_LIMIT_FALLBACK = 1;
var validateDisplayConfiguration = function validateDisplayConfiguration(displayConfig) {
  if (displayConfig.maxResults < 0) {
    throw "Invalid Display Configuration.  MaxResults set to [".concat(displayConfig.maxResults, "].  Needs to be >= 0");
  }
  Object.values(displayConfig.maxResultsPerTaxonomy).forEach(function (taxonomyMax) {
    if (taxonomyMax < 0) {
      throw "Invalid Display Configuration.  TaxonomyMaxResult set to negative number";
    }
  });
  if (displayConfig.taxonomyConfiguration) {
    Object.values(displayConfig.taxonomyConfiguration).forEach(function (taxonomyResultTypeConfiguration) {
      taxonomyResultTypeConfiguration.forEach(function (resultTypeConfiguration) {
        if (resultTypeConfiguration.maxResultsByResultType && resultTypeConfiguration.maxResultsByResultType < 0) {
          throw "Invalid Display Configuration.  ResultType: [".concat(resultTypeConfiguration.resultTypeId, "] maxResults set to negative number");
        }
      });
    });
  }
};
var generateTaxonomyConfiguration = function generateTaxonomyConfiguration(results, taxonomyId, displayConfig) {
  if (!results) {
    return {};
  }
  var taxonomyResultLimit = displayConfig.maxResultsPerTaxonomy[taxonomyId] || displayConfig.maxResultsPerTaxonomy['default'] || TAXONOMY_RESULT_LIMIT_FALLBACK;

  // Initialize the taxonomyDisplayConfig with the current set of results for each type in a taxonomy
  var resultsPerResultType = results.reduce(function (accum, current) {
    if (accum[current.resultType]) {
      accum[current.resultType] = ++accum[current.resultType];
    } else {
      accum[current.resultType] = 1;
    }
    return accum;
  }, {});
  var taxonomyDisplayConfiguration = {};

  // Update the taxonomyDisplayConfig
  // Use the specified taxonomyConfiguration if provided or set the config based on order of results
  if (displayConfig.taxonomyConfiguration && displayConfig.taxonomyConfiguration[taxonomyId]) {
    var resultsRemaining = taxonomyResultLimit;
    var taxonomyTypeConfig = displayConfig.taxonomyConfiguration[taxonomyId];
    Object.keys(resultsPerResultType).forEach(function (resultType) {
      var _taxonomyTypeConfig$f;
      var configuredLimit = (_taxonomyTypeConfig$f = taxonomyTypeConfig.find(function (resultConfig) {
        return resultConfig.resultTypeId === resultType;
      })) === null || _taxonomyTypeConfig$f === void 0 ? void 0 : _taxonomyTypeConfig$f.maxResultsByResultType;
      if (configuredLimit !== undefined) {
        taxonomyDisplayConfiguration[resultType] = Math.min(configuredLimit, resultsRemaining);
      } else {
        taxonomyDisplayConfiguration[resultType] = Math.min(resultsPerResultType[resultType], resultsRemaining);
      }
      resultsRemaining -= taxonomyDisplayConfiguration[resultType];
    });

    // Will override the specified type max results to the taxonomyResultLimit
    if (resultsRemaining > 0) {
      Object.keys(taxonomyDisplayConfiguration).forEach(function (resultType) {
        taxonomyDisplayConfiguration[resultType] = Math.min(taxonomyDisplayConfiguration[resultType] + resultsRemaining, resultsPerResultType[resultType]);
      });
    }
  } else {
    var taxonomyTypeIds = Object.keys(resultsPerResultType);
    var _resultsRemaining = taxonomyResultLimit;
    taxonomyTypeIds.forEach(function (type) {
      taxonomyDisplayConfiguration[type] = Math.min(resultsPerResultType[type], _resultsRemaining);
      _resultsRemaining -= taxonomyDisplayConfiguration[type];
    });
  }
  return taxonomyDisplayConfiguration;
};
var SearchResultsDisplay = function SearchResultsDisplay(_ref) {
  var resultDisplayConfiguration = _ref.resultDisplayConfiguration;
  var _useSearchContext = useSearchContext(),
    results = _useSearchContext.results,
    renderers = _useSearchContext.renderers,
    fireImpression = _useSearchContext.fireImpression,
    allResultsReturned = _useSearchContext.allResultsReturned,
    previousResults = _useSearchContext.previousResults;
    _useSearchContext.locale;
  var _useState = useState(previousResults),
    _useState2 = _slicedToArray(_useState, 2),
    currentResults = _useState2[0],
    setCurrentResults = _useState2[1];
  useEffect(function () {
    if (allResultsReturned) {
      setCurrentResults(results);
    }
  }, [allResultsReturned, results]);
  validateDisplayConfiguration(resultDisplayConfiguration);

  // Sort Taxonomies by specified taxonomyOrder.  Any non-fallback taxonomy with results not specified, will get added as they appear after
  var taxonomyOrder = resultDisplayConfiguration.taxonomyOrder;
  Object.keys(currentResults).forEach(function (taxonomy) {
    var _resultDisplayConfigu, _resultDisplayConfigu4, _resultDisplayConfigu5;
    var fallbackForCurrentTaxonomy = (_resultDisplayConfigu = resultDisplayConfiguration.taxonomyFallback) === null || _resultDisplayConfigu === void 0 ? void 0 : _resultDisplayConfigu[taxonomy];
    var primaryTaxonomy = Object.keys(resultDisplayConfiguration.taxonomyFallback || {}).find(function (key) {
      var _resultDisplayConfigu2, _resultDisplayConfigu3;
      return ((_resultDisplayConfigu2 = resultDisplayConfiguration.taxonomyFallback) === null || _resultDisplayConfigu2 === void 0 ? void 0 : _resultDisplayConfigu2[key]) === taxonomy || ((_resultDisplayConfigu3 = resultDisplayConfiguration.taxonomyFallback) === null || _resultDisplayConfigu3 === void 0 ? void 0 : _resultDisplayConfigu3[key]).fallbackTaxonomy === taxonomy;
    });
    var isFallbackTaxonomy = !!primaryTaxonomy;

    //case 1: processing primary, simple fallback taxonomy
    if (!!fallbackForCurrentTaxonomy && typeof fallbackForCurrentTaxonomy === 'string') {
      var _currentResults$fallb;
      //Replace if current (primary) has no results and found (fallback) does
      if (currentResults[taxonomy].length === 0 && (_currentResults$fallb = currentResults[fallbackForCurrentTaxonomy]) !== null && _currentResults$fallb !== void 0 && _currentResults$fallb.length) {
        currentResults[taxonomy] = currentResults[fallbackForCurrentTaxonomy];
      }
    }
    //case 2: processing fallback, must find primary, simple fallback
    else if (isFallbackTaxonomy && typeof ((_resultDisplayConfigu4 = resultDisplayConfiguration.taxonomyFallback) === null || _resultDisplayConfigu4 === void 0 ? void 0 : _resultDisplayConfigu4[primaryTaxonomy]) === 'string') {
      var _currentResults$prima, _currentResults$taxon;
      //Replace if found (primary) has no results but current (fallback) taxonomy does
      if (((_currentResults$prima = currentResults[primaryTaxonomy]) === null || _currentResults$prima === void 0 ? void 0 : _currentResults$prima.length) === 0 && (_currentResults$taxon = currentResults[taxonomy]) !== null && _currentResults$taxon !== void 0 && _currentResults$taxon.length) {
        currentResults[primaryTaxonomy] = currentResults[taxonomy];
      }
    }
    //case 3: processing primary, complex result replacement
    else if (!!fallbackForCurrentTaxonomy && _typeof(fallbackForCurrentTaxonomy) === 'object') {
      var _currentResults$taxon2;
      var filterFunction = function filterFunction(result) {
        return fallbackForCurrentTaxonomy.resultTypes.includes(result.resultType || '');
      };
      //Fill in results of specified types from found (fallback) if none are present in current (primary)
      if (!((_currentResults$taxon2 = currentResults[taxonomy]) !== null && _currentResults$taxon2 !== void 0 && _currentResults$taxon2.filter(filterFunction).length)) {
        var _currentResults$fallb2;
        currentResults[taxonomy] = (currentResults[taxonomy] || []).concat(((_currentResults$fallb2 = currentResults[fallbackForCurrentTaxonomy.fallbackTaxonomy]) === null || _currentResults$fallb2 === void 0 ? void 0 : _currentResults$fallb2.filter(filterFunction)) || []);
      }
    }
    //case 4: processing fallback, must find primary, complex result replacement
    else if (isFallbackTaxonomy && _typeof((_resultDisplayConfigu5 = resultDisplayConfiguration.taxonomyFallback) === null || _resultDisplayConfigu5 === void 0 ? void 0 : _resultDisplayConfigu5[primaryTaxonomy]) === 'object') {
      var _currentResults$prima2;
      var fallbackConfig = Object.values(resultDisplayConfiguration.taxonomyFallback).find(function (fallbackConfig) {
        return fallbackConfig.fallbackTaxonomy === taxonomy;
      });
      var _filterFunction = function _filterFunction(result) {
        return fallbackConfig.resultTypes.includes(result.resultType || '');
      };
      //Fill in results of specified types from current (fallback) if none are present in found (primary)
      if (!((_currentResults$prima2 = currentResults[primaryTaxonomy]) !== null && _currentResults$prima2 !== void 0 && _currentResults$prima2.filter(_filterFunction).length)) {
        currentResults[primaryTaxonomy] = (currentResults[primaryTaxonomy] || []).concat(currentResults[taxonomy].filter(_filterFunction) || []);
      }
    }
    if (!taxonomyOrder.includes(taxonomy) && !isFallbackTaxonomy) {
      taxonomyOrder.push(taxonomy);
    } else if (isFallbackTaxonomy && !taxonomyOrder.includes(primaryTaxonomy)) {
      taxonomyOrder.push(primaryTaxonomy);
    }
  });
  var renderedResults = 0;
  var availableSlots = resultDisplayConfiguration.maxResults;
  return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, taxonomyOrder.map(function (taxonomyId, index) {
    var _currentResults$taxon3, _resultDisplayConfigu6, _resultDisplayConfigu9;
    if (resultDisplayConfiguration.includeTaxonomies && !resultDisplayConfiguration.includeTaxonomies.includes(taxonomyId)) {
      return null;
    }
    //Sort results within a taxonomy by type if specified, otherwise leave results alone
    var unfilteredResults = (_currentResults$taxon3 = currentResults[taxonomyId]) !== null && _currentResults$taxon3 !== void 0 && _currentResults$taxon3.length ? (_resultDisplayConfigu6 = resultDisplayConfiguration.resultTypeOrderingPerTaxonomy[taxonomyId]) !== null && _resultDisplayConfigu6 !== void 0 && _resultDisplayConfigu6.length ? sortResultsByType(currentResults[taxonomyId], resultDisplayConfiguration.resultTypeOrderingPerTaxonomy[taxonomyId]) : currentResults[taxonomyId] : undefined;
    var taxonomyResults = unfilteredResults === null || unfilteredResults === void 0 ? void 0 : unfilteredResults.filter(function (result) {
      return !result.algoliaResult || !result.algoliaResult.shouldHide;
    });
    var currType = (unfilteredResults === null || unfilteredResults === void 0 ? void 0 : unfilteredResults[0].resultType) || '',
      indexInType = 0;
    var abTestHiddenResults = unfilteredResults === null || unfilteredResults === void 0 ? void 0 : unfilteredResults.map(function (result, index) {
      var _result$algoliaResult;
      if (!((_result$algoliaResult = result.algoliaResult) !== null && _result$algoliaResult !== void 0 && (_result$algoliaResult = _result$algoliaResult.hideInTests) !== null && _result$algoliaResult !== void 0 && _result$algoliaResult.length)) {
        return undefined;
      }
      if (result.resultType !== currType) {
        indexInType = 0;
        currType = result.resultType;
      }
      var hiddenResult = {
        tests: result.algoliaResult.hideInTests,
        index: index,
        indexInType: indexInType,
        type: result.resultType
      };
      indexInType++;
      return hiddenResult;
    }).filter(function (hiddenResult) {
      return hiddenResult;
    });
    if (!(taxonomyResults !== null && taxonomyResults !== void 0 && taxonomyResults.length) && renderedResults < resultDisplayConfiguration.maxResults && resultDisplayConfiguration.maxResultsPerTaxonomy[taxonomyId] > 0) {
      abTestHiddenResults === null || abTestHiddenResults === void 0 || abTestHiddenResults.filter(function (result) {
        var _resultDisplayConfigu7, _resultDisplayConfigu8;
        return (_resultDisplayConfigu7 = (_resultDisplayConfigu8 = resultDisplayConfiguration.taxonomyConfiguration) === null || _resultDisplayConfigu8 === void 0 ? void 0 : _resultDisplayConfigu8[taxonomyId].find(function (config) {
          return config.resultTypeId === (result === null || result === void 0 ? void 0 : result.type);
        })) !== null && _resultDisplayConfigu7 !== void 0 ? _resultDisplayConfigu7 : 1 > 0;
      }).flatMap(function (result) {
        return result.tests;
      }).forEach(function (test) {
        return fireImpression(test);
      });
    }
    var taxonomyConfiguration = generateTaxonomyConfiguration(taxonomyResults, taxonomyId, resultDisplayConfiguration);

    //Limit rendered results to maxResults
    var numResultsToRender = Object.values(taxonomyConfiguration).reduce(function (total, maxResultsPerType) {
      return total += maxResultsPerType;
    }, 0);
    if (numResultsToRender + renderedResults > resultDisplayConfiguration.maxResults) {
      Object.keys(taxonomyConfiguration).forEach(function (taxonomyType) {
        var remainingResultsToRender = resultDisplayConfiguration.maxResults - renderedResults;
        taxonomyConfiguration[taxonomyType] = Math.min(taxonomyConfiguration[taxonomyType], remainingResultsToRender);
        renderedResults += taxonomyConfiguration[taxonomyType];
      });
    } else {
      renderedResults += numResultsToRender;
    }
    var willRenderResults = Object.values(taxonomyConfiguration).reduce(function (total, maxResultsPerType) {
      return total += maxResultsPerType;
    }, 0) > 0;
    if (!(taxonomyResults !== null && taxonomyResults !== void 0 && taxonomyResults.length) && ((_resultDisplayConfigu9 = resultDisplayConfiguration.maxResultsPerTaxonomy[taxonomyId]) !== null && _resultDisplayConfigu9 !== void 0 ? _resultDisplayConfigu9 : resultDisplayConfiguration.maxResultsPerTaxonomy['default'] > 0) && renderedResults < resultDisplayConfiguration.maxResults) {
      abTestHiddenResults === null || abTestHiddenResults === void 0 || abTestHiddenResults.reduce(function (acc, result) {
        var _resultDisplayConfigu10, _resultDisplayConfigu11;
        if ((_resultDisplayConfigu10 = (_resultDisplayConfigu11 = resultDisplayConfiguration.taxonomyConfiguration) === null || _resultDisplayConfigu11 === void 0 || (_resultDisplayConfigu11 = _resultDisplayConfigu11[taxonomyId]) === null || _resultDisplayConfigu11 === void 0 || (_resultDisplayConfigu11 = _resultDisplayConfigu11.find(function (config) {
          return config.resultTypeId === result.type;
        })) === null || _resultDisplayConfigu11 === void 0 ? void 0 : _resultDisplayConfigu11.maxResultsByResultType) !== null && _resultDisplayConfigu10 !== void 0 ? _resultDisplayConfigu10 : 1 > 0) {
          acc.push(result);
        }
        return acc;
      }, []).flatMap(function (result) {
        return result.tests;
      }).forEach(function (test) {
        return fireImpression(test);
      });
    }
    if (taxonomyResults !== null && taxonomyResults !== void 0 && taxonomyResults.length && renderers[taxonomyId] && willRenderResults) {
      var _abTestHiddenResults$;
      var maxResults = Math.min(resultDisplayConfiguration.maxResultsPerTaxonomy[taxonomyId] || resultDisplayConfiguration.maxResultsPerTaxonomy['default'], currentResults[taxonomyId].length, availableSlots);
      availableSlots -= maxResults;
      var hiddenSoFar = 0;
      currType = (abTestHiddenResults === null || abTestHiddenResults === void 0 || (_abTestHiddenResults$ = abTestHiddenResults[0]) === null || _abTestHiddenResults$ === void 0 ? void 0 : _abTestHiddenResults$.type) || '';
      abTestHiddenResults === null || abTestHiddenResults === void 0 || abTestHiddenResults.reduce(function (acc, result) {
        if (currType !== result.type) {
          currType = result.type;
          hiddenSoFar = 0;
        }
        if (result.index - hiddenSoFar < taxonomyConfiguration[result.type]) {
          acc.push(result);
          hiddenSoFar++;
        }
        return acc;
      }, []).flatMap(function (result) {
        return result.tests;
      }).forEach(function (test) {
        return fireImpression(test);
      });
      return /*#__PURE__*/React__default.createElement(ListItem, {
        role: "presentation",
        p: 0,
        m: 0,
        key: taxonomyId,
        className: taxonomyId == CustomerWorkResultTaxonomy.id ? 'search-flyout-customer-work-result-section' : 'search-results-section',
        backgroundColor: taxonomyId == CustomerWorkResultTaxonomy.id ? 'strong' : undefined,
        paddingX: 6,
        pt: index === 1 ? 2 : taxonomyId == CustomerWorkResultTaxonomy.id ? 5 : 0,
        pb: taxonomyId == CustomerWorkResultTaxonomy.id ? 5 : undefined,
        marginBottom: taxonomyId == CustomerWorkResultTaxonomy.id ? 4 : undefined
      }, /*#__PURE__*/React__default.createElement(SectionAnalyticsWrapper, {
        taxonomyId: taxonomyId,
        sectionName: taxonomyId
      }, /*#__PURE__*/React__default.createElement(renderers[taxonomyId], {
        results: taxonomyResults,
        maxResults: maxResults,
        key: taxonomyId,
        headerId: "".concat(taxonomyId.replace(' ', ''), "-label"),
        taxonomyConfiguration: taxonomyConfiguration,
        fillToMaxResults: resultDisplayConfiguration.fillToMaxResults
      })));
    } else {
      return null;
    }
  }));
};

export { SearchResultsDisplay };
//# sourceMappingURL=SearchResultsDisplay.js.map
